import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

/**
 * Universal Logo Component
 * Supports both image-based and text-based logos with fallback
 * @param {number} size - Logo size in pixels (default: 40)
 * @param {boolean} showText - Show text next to logo (default: false)
 * @param {string} variant - Color variant: "primary" or "inherit" (default: "primary")
 * @param {number} textScale - Scale factor for text (default: 1.0, use 0.75 for sidebar)
 * @param {string} type - Logo type: "icon" | "horizontal" | "vertical" | "text" (default: "text")
 * @param {boolean} useImage - Force image logo instead of text (default: false)
 */
export default function Logo({ 
  size = 40, 
  showText = false, 
  variant = "primary", 
  textScale = 1.0,
  type = "text",
  useImage = false 
}) {
  const theme = useTheme();

  const logoColor = variant === "primary" ? "primary.main" : "text.primary";
  const scale = textScale;

  // Image logo paths
  const logoImages = {
    icon: "/images/karsanusa/karsanusa-logo-only.png",
    horizontal: "/images/karsanusa/karsanusa-logo.png",
    vertical: "/images/karsanusa/karsanusa-logo-vertical.png",
  };

  // Determine which image to use
  const getImageSrc = () => {
    if (type === "icon") return logoImages.icon;
    if (type === "horizontal") return logoImages.horizontal;
    if (type === "vertical") return logoImages.vertical;
    return logoImages.icon; // default fallback
  };

  // Calculate appropriate size for image logos
  const getImageSize = () => {
    if (type === "horizontal") return { width: size * 2.5, height: size * 0.8 };
    if (type === "vertical") return { width: size * 1.2, height: size * 1.2 };
    return { width: size, height: size };
  };

  // If using image logo or useImage is true
  if (useImage || type !== "text") {
    const imageSize = getImageSize();
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: showText ? 1 : 0,
        }}
      >
        <Box
          component="img"
          src={getImageSrc()}
          alt="KARSA NUSA Logo"
          sx={{
            width: imageSize.width,
            height: imageSize.height,
            objectFit: "contain",
          }}
        />
      </Box>
    );
  }

  // Text-based logo (original implementation as fallback)
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: showText ? 1 : 0,
      }}
    >
      {/* Logo Icon - Stylized "KN" with modern design */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          bgcolor: logoColor,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 85, 149, 0.3)",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            color: "white",
            fontSize: size * 0.45,
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          KN
        </Typography>
      </Box>

      {/* Optional Text Label */}
      {showText && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            lineHeight: 1.1,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={500}
            sx={{
              color: logoColor,
              fontSize: size * 0.32 * scale,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            Karsa Nusa
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              color: logoColor,
              fontSize: size * 0.48 * scale,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            Submission System
          </Typography>
        </Box>
      )}
    </Box>
  );
}
